import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import { Send } from 'lucide-react';
import { useAssistantChat, useNow } from '../../hooks/assistant/useAssistantChats';
import { orderedTurns, pendingView } from '../../Helpers/assistantChatView';
import AssistantAnswer, { AssistantRefLink } from './AssistantAnswer';
import AssistantTurnFooter from './AssistantTurnFooter';

const MAX_QUESTION_CHARS = 4000;

interface Props {
    chatKey: string;
    contextBuildingKey?: string;
    sending: boolean;
    error: string | null;
    onAsk: (question: string) => Promise<void>;
    onFeedback: (turnKey: string, rating: 'up' | 'down', comment?: string) => Promise<boolean>;
    onNavigate: () => void;
}

/** One chat: earlier turns, the answer being produced, and the question box. */
const AssistantConversation: React.FC<Props> = ({ chatKey, contextBuildingKey, sending, error, onAsk, onFeedback, onNavigate }) => {
    const chatState = useAssistantChat(chatKey);
    const chat = chatState.status === 'loaded' ? chatState.value : null;
    const hasPending = !!chat?.pending;
    const now = useNow(hasPending);
    const pending = pendingView(chat, now);
    const turns = orderedTurns(chat);
    const busy = sending || pending.state === 'running';

    const [question, setQuestion] = useState('');
    const [lastSent, setLastSent] = useState('');
    const bottomRef = useRef<HTMLDivElement>(null);

    const pendingText = pending.state === 'running' ? pending.text : '';
    const pendingSteps = pending.state === 'running' ? pending.steps.length : 0;
    useEffect(() => {
        bottomRef.current?.scrollIntoView?.({ block: 'end' });
    }, [turns.length, pendingText, pendingSteps, sending]);

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        const trimmed = question.trim();
        if (!trimmed || busy) return;
        setQuestion('');
        setLastSent(trimmed);
        await onAsk(trimmed);
    };

    const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            e.currentTarget.form?.requestSubmit();
        }
    };

    // The question just sent, before the function has written `pending`.
    const awaitingPending = sending && pending.state === 'none';

    return (
        <div className="assistant-conversation">
            <div className="assistant-messages">
                {chatState.status === 'error' && <Alert variant="danger">Chatten kunde inte läsas.</Alert>}
                {chatState.status === 'loaded' && turns.length === 0 && pending.state === 'none' && !sending && (
                    <div className="assistant-empty">
                        <p>Ställ en fråga om fastigheterna, till exempel:</p>
                        <ul>
                            <li>Hur många av byggnaderna har betongtak?</li>
                            <li>När var senast någon i byggnaden, och vad gjordes då?</li>
                            <li>Vi ska åka ut hit, är det något vi bör tänka på när vi ändå är där?</li>
                        </ul>
                        {contextBuildingKey && (
                            <p className="assistant-context-note">
                                Öppen byggnad: <AssistantRefLink assistantRef={{ kind: 'building', keys: [contextBuildingKey], raw: `{building:${contextBuildingKey}}` }} />
                            </p>
                        )}
                    </div>
                )}

                {turns.map((turn) => (
                    <div key={turn.key} className="assistant-turn">
                        <div className="assistant-question">{turn.question}</div>
                        <div className={`assistant-reply ${turn.diagnostics.stopReason === 'error' ? 'assistant-reply--error' : ''}`}>
                            <AssistantAnswer text={turn.answer} onNavigate={onNavigate} />
                            {turn.diagnostics.stopReason === 'limit_reached' && (
                                <div className="assistant-reply-note">Svaret kan vara ofullständigt.</div>
                            )}
                            <AssistantTurnFooter turn={turn} onFeedback={onFeedback} />
                        </div>
                    </div>
                ))}

                {(pending.state === 'running' || awaitingPending) && (
                    <div className="assistant-turn">
                        <div className="assistant-question">{pending.state === 'running' ? pending.question : lastSent}</div>
                        <div className="assistant-reply assistant-reply--pending">
                            <ul className="assistant-steps" aria-live="polite">
                                {pending.state === 'running' && pending.steps.map((step, i) => <li key={i}>{step}</li>)}
                                <li className="assistant-steps-current"><Spinner animation="border" size="sm" /> Arbetar…</li>
                            </ul>
                            {pendingText && <AssistantAnswer text={pendingText} onNavigate={onNavigate} />}
                        </div>
                    </div>
                )}

                {pending.state === 'failed' && (
                    <div className="assistant-turn">
                        <div className="assistant-question">{pending.question}</div>
                        <div className="assistant-reply assistant-reply--error">Svaret misslyckades. Försök igen.</div>
                    </div>
                )}

                {error && <Alert variant="danger" className="assistant-error">{error}</Alert>}
                <div ref={bottomRef} />
            </div>

            <Form className="assistant-input" onSubmit={submit}>
                <Form.Control
                    as="textarea"
                    rows={2}
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    onKeyDown={onKeyDown}
                    placeholder={busy ? 'Väntar på svar…' : 'Ställ en fråga…'}
                    aria-label="Fråga"
                    maxLength={MAX_QUESTION_CHARS}
                    disabled={busy}
                />
                <Button type="submit" aria-label="Skicka" disabled={busy || !question.trim()}>
                    <Send size={18} />
                </Button>
            </Form>
        </div>
    );
};

export default AssistantConversation;
